import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import WizardDateField from './WizardDateField';
import { fr } from 'date-fns/locale';
import { X, Check, Camera, Upload, Pencil, ChevronRight, ChevronDown, Link2, Banknote, CreditCard, ArrowLeft, ArrowRight, FileText, Wallet, Info, Trash2, RefreshCw } from 'lucide-react';
import { Input } from './ui/input';
import { toast } from 'sonner';
import AmountInput from './AmountInput';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const STEPS = [
  { num: 1, label: 'Scan' },
  { num: 2, label: 'Détails' },
  { num: 3, label: 'Montant' },
  { num: 4, label: 'Validation' },
];

const TVA_OPTIONS = [
  { label: '0%', value: 0 },
  { label: '9%', value: 9 },
  { label: '19%', value: 19 },
];

const PAYMENT_METHODS_LEGACY = [
  { label: 'Espèces', value: 'cash' },
  { label: 'Virement bancaire', value: 'transfer' },
  { label: 'Chèque', value: 'check' },
  { label: 'Carte', value: 'card' },
];

const PAY_METHODS = [
  { id: 'cash', label: 'Espèces', icon: Banknote },
  { id: 'card', label: 'Carte', icon: CreditCard },
  { id: 'transfer', label: 'Virement', icon: ArrowRight },
  { id: 'check', label: 'Chèque', icon: FileText },
  { id: 'traite', label: 'Traite', icon: Wallet },
];

// Blue color scheme
const C = '#2563EB';
const C_LIGHT = '#EFF6FF';
const C_BG = '#F0F7FF';

function InvoiceWizard({ open, onClose, onSuccess, currentUser, editData }) {
  const [step, setStep] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [bls, setBls] = useState([]);
  const [matchedBL, setMatchedBL] = useState(null);
  const [blLinked, setBlLinked] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [tvaOpen, setTvaOpen] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  // Invoice scan/attachment
  const [attachment, setAttachment] = useState(null); // { file, preview, url, filename, type }
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const [form, setForm] = useState({
    supplier_id: '', supplier_name: '', invoice_number: '', payment_method: 'transfer',
    date: format(new Date(), 'yyyy-MM-dd'), due_date: '', notes: '', bl_number: '',
    document_type: 'facture', delivery_date: '',
    amount_ht: '', tva_rate: 0,
    // Payment fields
    pay_state: 'unpaid', // 'unpaid' | 'paid' | 'partial'
    paid_amount: '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    pay_method: 'cash',
    payment_method: 'cash',
  });

  const headers = { 'X-User-Id': currentUser?.id || '' };

  useEffect(() => {
    if (open) {
      fetchSuppliers(); fetchBLs();
      // Reset attachment for fresh creation; preload for edit if present
      setAttachment(editData?.invoice_image_url ? {
        file: null,
        preview: null,
        url: editData.invoice_image_url,
        filename: editData.invoice_image_url.split('/').pop() || 'document',
        type: editData.invoice_image_url.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/*',
      } : null);
      if (editData) {
        const paidAmt = editData.paid_amount || 0;
        const payState = paidAmt >= (editData.amount || 0) && (editData.amount || 0) > 0 ? 'paid' : paidAmt > 0 ? 'partial' : 'unpaid';
        setForm({
          supplier_id: editData.supplier_id || '', supplier_name: editData.supplier_name || '',
          invoice_number: editData.invoice_number || '',
          payment_method: editData.payment_method || 'cash',
          date: editData.date ? format(parseSafeDate(editData.date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
          due_date: editData.due_date || '',
          notes: editData.notes || '', bl_number: editData.bl_number || '',
          document_type: editData.document_type || 'facture',
          delivery_date: editData.delivery_date || '',
          amount_ht: String(editData.amount_ht || editData.amount || ''),
          tva_rate: editData.tva_rate || 0,
          pay_state: payState,
          paid_amount: paidAmt > 0 ? String(paidAmt) : '',
          payment_date: format(new Date(), 'yyyy-MM-dd'),
          pay_method: editData.payment_method || 'cash',
        });
        setStep(2);
        setMatchedBL(null); setBlLinked(!!editData.bl_number);
      } else {
        setStep(1); setMatchedBL(null); setBlLinked(false);
        setForm({
          supplier_id: '', supplier_name: '', invoice_number: '', payment_method: 'cash',
          date: format(new Date(), 'yyyy-MM-dd'), due_date: '', notes: '', bl_number: '',
          document_type: 'facture', delivery_date: '',
          amount_ht: '', tva_rate: 0,
          pay_state: 'unpaid', paid_amount: '',
          payment_date: format(new Date(), 'yyyy-MM-dd'), pay_method: 'cash',
        });
      }
    }
  }, [open]);

  const fetchSuppliers = async () => { try { const r = await axios.get(`${API}/suppliers`, { headers: { ...headers, 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }, params: { _t: Date.now() } }); setSuppliers(r.data); } catch {} };
  const fetchBLs = async () => { try { const r = await axios.get(`${API}/factures-bl-paiements`, { headers: { ...headers, 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }, params: { start_date: new Date(2020,0,1).toISOString(), end_date: new Date().toISOString(), pharmacy_ids: 'all', _t: Date.now() } }); setBls(r.data?.bls || []); } catch {} };

  useEffect(() => {
    if (form.supplier_id && bls.length > 0) {
      const sup = suppliers.find(s => s.id === form.supplier_id);
      if (sup) { const m = bls.find(b => b.supplier === sup.name && !b.invoice_number); setMatchedBL(m || null); }
    }
  }, [form.supplier_id, bls, suppliers]);

  const tvaAmount = Math.round((parseFloat(form.amount_ht) || 0) * (form.tva_rate / 100));
  const totalTTC = (parseFloat(form.amount_ht) || 0) + tvaAmount;
  const fmt = (v) => new Intl.NumberFormat('fr-DZ').format(v);

  // Payment amount helper
  const getPayAmount = () => {
    if (form.pay_state === 'paid') return parseFloat(form.paid_amount) || totalTTC;
    if (form.pay_state === 'partial') return parseFloat(form.paid_amount) || 0;
    return 0;
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    // 1) Validate type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez JPG, PNG, WEBP ou PDF.');
      return;
    }
    // 2) Validate size (10 MB)
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error('Fichier trop volumineux (max 10 Mo).');
      return;
    }

    // 3) Build local preview (sync, instant)
    let previewDataUrl = null;
    if (file.type.startsWith('image/')) {
      previewDataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      }).catch(() => null);
    }

    // 4) Upload to backend
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload-invoice`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.url;
      const filename = file.name || res.data?.filename;
      // Get the backend filename for extraction
      const serverFilename = res.data?.filename;
      
      setAttachment({
        file,
        preview: previewDataUrl,
        url,
        filename,
        type: file.type,
      });
      toast.success('Document attaché');
      
      // Attempt extraction
      if (serverFilename) {
        setExtracting(true);
        toast.info('Analyse du document par IA en cours...');
        try {
          const extractRes = await axios.post(`${API}/extract-invoice`, { filename: serverFilename }, { headers });
          const data = extractRes.data;
          
          if (data) {
            setForm(prev => {
              const newForm = { ...prev };
              if (data.invoice_number) newForm.invoice_number = data.invoice_number;
              if (data.date) newForm.date = data.date;
              if (data.tva !== null && data.tva !== undefined) newForm.tva_rate = data.tva;
              
              if (data.amount) {
                // If we got total TTC, we can compute HT if TVA is present
                const totalTtc = parseFloat(data.amount);
                const tvaRate = newForm.tva_rate || 0;
                if (totalTtc) {
                  const ht = totalTtc / (1 + tvaRate / 100);
                  newForm.amount_ht = ht.toFixed(2);
                }
              }
              
              // Fuzzy match supplier name
              if (data.supplier_name && suppliers.length > 0) {
                const searchName = data.supplier_name.toLowerCase();
                // Find a supplier that includes the extracted name or vice versa
                const match = suppliers.find(s => 
                  s.name.toLowerCase().includes(searchName) || 
                  searchName.includes(s.name.toLowerCase())
                );
                if (match) {
                  newForm.supplier_id = match.id;
                  newForm.supplier_name = match.name;
                } else {
                  // Keep the extracted name in notes if no match
                  newForm.notes = prev.notes ? `${prev.notes}\nFournisseur extrait: ${data.supplier_name}` : `Fournisseur extrait: ${data.supplier_name}`;
                }
              }
              
              return newForm;
            });
            toast.success('Données extraites avec succès !');
          }
        } catch (extErr) {
          const detail = extErr.response?.data?.detail;
          console.error("Extraction error details:", detail || extErr);
          toast.error(detail || "L'extraction automatique a échoué, veuillez remplir manuellement.");
        } finally {
          setExtracting(false);
        }
      }
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const clearAttachment = () => {
    setAttachment(null);
    if (fileRef.current) fileRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  const handleSubmit = async () => {
    const paidAmount = getPayAmount();
    const resolvedBlNumber = form.bl_number || (blLinked && matchedBL ? matchedBL.id : '');
    
    // Safely parse date to ISO string
    let parsedDate = new Date();
    if (form.date) {
      const d = parseSafeDate(form.date);
      if (!isNaN(d.getTime())) parsedDate = d;
    }

    const payload = {
      supplier_id: form.supplier_id, date: parsedDate.toISOString(),
      invoice_number: form.invoice_number, bl_number: resolvedBlNumber,
      document_type: form.document_type || 'facture',
      delivery_date: form.delivery_date || null,
      amount: totalTTC, amount_ht: parseFloat(form.amount_ht) || 0, tva_rate: form.tva_rate, tva_amount: tvaAmount,
      payment_method: form.pay_method || form.payment_method,
      due_date: form.due_date, source: 'manual', notes: form.notes,
      paid_amount: paidAmount,
      payment_date: form.pay_state !== 'unpaid' ? (form.document_type === 'bl' ? (form.delivery_date || form.date) : (form.due_date || form.date)) : null,
      payment_status: form.pay_state,
      invoice_image_url: attachment?.url || null,
    };

    try {
      if (editData && editData.id) {
        await axios.put(`${API}/suppliers/${form.supplier_id}/invoices/${editData.id}`, payload, { headers });
        toast.success('Facture modifiée !');
      } else {
        await axios.post(`${API}/suppliers/${form.supplier_id}/invoices`, payload, { headers });
        toast.success('Facture créée !');
      }
      onSuccess(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (!open) return null;

  const canNext = () => {
    if (step === 2) return !!form.supplier_id;
    if (step === 3) return parseFloat(form.amount_ht) > 0;
    return true;
  };

  const Badge = () => (
    <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ backgroundColor: C_LIGHT, color: C }}>IA</span>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>

        {/* Hidden inputs for camera & file */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files?.[0])}
          data-testid="invoice-camera-input"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files?.[0])}
          data-testid="invoice-file-input"
        />

        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: C_LIGHT }}>
              <FileText className="w-5 h-5" style={{ color: C }} />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{editData && editData.id ? 'Modifier la facture' : editData ? 'Compléter en facture' : 'Nouvelle entrée'}</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">{format(new Date(), 'dd MMM. yyyy', { locale: fr })}</span>
            <button data-testid="invoice-wizard-close" onClick={onClose} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Stepper */}
        <div className="px-5 py-3 bg-white border-b border-gray-100">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.num}>
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= s.num ? 'text-white' : 'bg-gray-200 text-gray-500'
                  }`} style={step >= s.num ? { backgroundColor: C } : {}}>
                    {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                  </div>
                  <span className="text-xs font-medium" style={{ color: step >= s.num ? C : '#9CA3AF' }}>{s.label}</span>
                </div>
                {i < 3 && <div className="flex-1 h-0.5 mx-2 rounded" style={{ backgroundColor: step > s.num ? C : '#E5E7EB' }} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5" style={{ WebkitOverflowScrolling: 'touch' }}>

          {/* ===== STEP 1 ===== */}
          {step === 1 && (<>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Ajouter un document</h3>
              <p className="text-sm text-gray-500 mt-1">Photo, fichier ou saisie — l'IA détecte et lit automatiquement</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => cameraRef.current?.click()}
                disabled={uploading}
                data-testid="invoice-camera-btn"
                className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center gap-3 hover:border-blue-300 hover:bg-blue-50/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: C_LIGHT }}>
                  <Camera className="w-6 h-6" style={{ color: C }} />
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-gray-900">Prendre une photo</p>
                  <p className="text-xs text-gray-500">Caméra</p>
                </div>
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading || extracting}
                data-testid="invoice-file-btn"
                className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center gap-3 hover:border-blue-300 hover:bg-blue-50/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: C_LIGHT }}>
                  <Upload className="w-6 h-6" style={{ color: C }} />
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-gray-900">Importer un fichier</p>
                  <p className="text-xs text-gray-500">PDF, image, Excel</p>
                </div>
              </button>
            </div>

            {/* Upload feedback */}
            {uploading && (
              <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 flex items-center gap-3" data-testid="invoice-upload-loading">
                <div className="w-5 h-5 rounded-full border-2 border-blue-300 border-t-blue-600 animate-spin" />
                <span className="text-sm text-blue-700 font-medium">Téléversement en cours…</span>
              </div>
            )}

            {/* Attachment preview */}
            {attachment && !uploading && (
              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 flex items-center gap-3" data-testid="invoice-attachment-preview">
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-white border border-gray-200 flex items-center justify-center">
                  {attachment.preview ? (
                    <img src={attachment.preview} alt="Aperçu" className="w-full h-full object-cover" />
                  ) : (
                    <FileText className="w-7 h-7 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate" data-testid="invoice-attachment-filename">{attachment.filename}</p>
                  <p className="text-xs text-gray-500">{attachment.type === 'application/pdf' ? 'PDF' : 'Image'} • Document attaché</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={extracting}
                    data-testid="invoice-attachment-replace"
                    className="w-9 h-9 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                    title="Remplacer"
                  >
                    {extracting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={clearAttachment}
                    data-testid="invoice-attachment-remove"
                    className="w-9 h-9 rounded-lg border border-red-200 bg-white flex items-center justify-center text-red-500 hover:bg-red-50"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            <button onClick={() => setStep(2)} className="w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all" style={{ borderColor: C, backgroundColor: C_BG }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: C_LIGHT }}>
                  <Pencil className="w-5 h-5" style={{ color: C }} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-gray-900">Saisie manuelle</p>
                  <p className="text-xs text-gray-500">Remplir moi-même</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </>)}

          {/* ===== STEP 2 ===== */}
          {step === 2 && (<>
            {/* BL Auto-match */}
            {matchedBL && !blLinked && (
              <div className="rounded-xl border-2 p-4" style={{ borderColor: C, backgroundColor: C_BG }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: C_LIGHT }}>
                    <Link2 className="w-4 h-4" style={{ color: C }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Liaison détectée automatiquement</p>
                    <p className="text-xs text-gray-500">Mizan a trouvé un BL correspondant à cette facture</p>
                  </div>
                </div>
                <div className="bg-white/60 rounded-lg p-3 text-sm space-y-1.5 mb-3">
                  <div className="flex justify-between"><span className="text-gray-500">BL trouvé</span><span><span className="font-bold px-2 py-0.5 rounded text-xs" style={{ backgroundColor: C_LIGHT, color: C }}>BL-{matchedBL.id?.slice(0,3)}</span></span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Fournisseur</span><span className="font-medium">{matchedBL.supplier}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Montant BL</span><span className="font-medium">{fmt(matchedBL.amount)} DA</span></div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => { setBlLinked(true); setForm(f => ({...f, bl_number: matchedBL.id, amount_ht: String(matchedBL.amount)})); }} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium bg-white hover:bg-gray-50">Confirmer la liaison</button>
                  <button onClick={() => setMatchedBL(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-500 bg-transparent hover:bg-gray-50">Ignorer</button>
                </div>
              </div>
            )}

            <div>
              <h3 className="text-xl font-bold text-gray-900">Détails du document</h3>
              <p className="text-sm text-gray-500 mt-1">Sélectionnez le type et complétez les informations</p>
            </div>

            {/* === Toggle TYPE DE DOCUMENT === */}
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Type de document <Badge /></label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, document_type: 'facture' }))}
                  data-testid="doc-type-facture"
                  className={`px-4 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
                    form.document_type === 'facture'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  Facture
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, document_type: 'bl' }))}
                  data-testid="doc-type-bl"
                  className={`px-4 py-3 rounded-xl border-2 font-semibold text-sm transition-all ${
                    form.document_type === 'bl'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:bg-blue-50'
                  }`}
                >
                  Bon de livraison (BL)
                </button>
              </div>
              <div className="mt-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 text-xs text-blue-700">
                {form.document_type === 'facture'
                  ? 'Document fiscal avec échéance de paiement'
                  : "Bon de livraison à convertir en facture ultérieurement"}
              </div>
            </div>

            {/* Fournisseur */}
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Fournisseur <Badge /></label>
              <div className="relative mt-1">
                <button onClick={() => setSupplierOpen(!supplierOpen)} className="w-full flex items-center justify-between px-4 py-3 border rounded-xl bg-white" style={{ borderColor: supplierOpen ? C : '#E5E7EB' }}>
                  <span className={form.supplier_name ? 'text-gray-900 font-medium' : 'text-gray-400'}>{form.supplier_name || 'Sélectionner...'}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                {supplierOpen && (<>
                  <div className="fixed inset-0 z-10" onClick={() => setSupplierOpen(false)} />
                  <div className="absolute top-full mt-1 left-0 right-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                    {Array.from(new Map(suppliers.filter(s => s && s.name).map(s => [s.name.trim().toLowerCase(), s])).values())
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(s => (
                      <button key={s.id} onClick={() => { setForm(f => ({...f, supplier_id: s.id, supplier_name: s.name})); setSupplierOpen(false); }} className="w-full text-left px-4 py-2.5 hover:bg-blue-50 text-sm">{s.name}</button>
                    ))}
                  </div>
                </>)}
              </div>
            </div>

            {/* N° + Date principale */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                  {form.document_type === 'bl' ? 'N° de BL' : 'N° de Facture'} <Badge />
                </label>
                {form.document_type === 'bl' ? (
                  <Input
                    value={form.bl_number}
                    onChange={e => setForm(f => ({ ...f, bl_number: e.target.value }))}
                    placeholder="BL-2026-0188"
                    className="mt-1 rounded-xl border-gray-200"
                  />
                ) : (
                  <Input
                    value={form.invoice_number}
                    onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))}
                    placeholder="FAC-2026-0042"
                    className="mt-1 rounded-xl border-gray-200"
                  />
                )}
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                  {form.document_type === 'bl' ? 'Date du BL' : 'Date de facture'} <Badge />
                </label>
                <div className="mt-1">
                  <WizardDateField
                    value={form.date ? new Date(form.date + 'T00:00:00') : null}
                    onChange={(d) => setForm(f => ({...f, date: d ? format(d, 'yyyy-MM-dd') : ''}))}
                    theme="blue"
                    testid="invoice-date-input"
                  />
                </div>
              </div>
            </div>

            {/* Échéance OU Date de livraison selon type */}
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                {form.document_type === 'bl' ? 'Date de livraison' : 'Échéance'} <Badge />
              </label>
              <div className="mt-1">
                {form.document_type === 'bl' ? (
                  <WizardDateField
                    value={form.delivery_date ? new Date(form.delivery_date + 'T00:00:00') : null}
                    onChange={(d) => setForm(f => ({ ...f, delivery_date: d ? format(d, 'yyyy-MM-dd') : '' }))}
                    theme="blue"
                    testid="invoice-delivery-date-input"
                  />
                ) : (
                  <WizardDateField
                    value={form.due_date ? new Date(form.due_date + 'T00:00:00') : null}
                    onChange={(d) => setForm(f => ({ ...f, due_date: d ? format(d, 'yyyy-MM-dd') : '' }))}
                    theme="blue"
                    testid="invoice-due-date-input"
                  />
                )}
              </div>
            </div>

            {/* Mode de paiement */}
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Mode de paiement</label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {PAY_METHODS.map((method) => {
                  const Icon = method.icon;
                  const isSelected = form.pay_method === method.id;
                  return (
                    <button key={method.id} type="button" data-testid={`invoice-pay-method-${method.id}`}
                      onClick={() => setForm(f => ({...f, pay_method: method.id, payment_method: method.id}))}
                      className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                        isSelected ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}>
                      <Icon size={18} />
                      <span className="text-[10px] font-semibold">{method.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide">Notes</label>
              <Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Observations..." className="mt-1 rounded-xl border-gray-200" />
            </div>
          </>)}

          {/* ===== STEP 3 ===== */}
          {step === 3 && (<>
            {/* Invoice reference header */}
            <div className="rounded-xl p-3 border flex items-center gap-3" style={{ backgroundColor: C_BG, borderColor: '#BFDBFE' }}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C_LIGHT }}>
                <FileText className="w-5 h-5" style={{ color: C }} />
              </div>
              <div>
                <div className="text-xs font-medium" style={{ color: C }}>Montant de la facture</div>
                <div className="text-sm font-bold text-gray-900">{form.invoice_number || '—'} &middot; {form.supplier_name || '—'}</div>
              </div>
            </div>

            {/* Amount input for main amount (or partial payment amount) */}
            {form.pay_state === 'partial' ? (
              <>
                <AmountInput
                  value={parseFloat(form.amount_ht) || 0}
                  onChange={(v) => setForm(f => ({...f, amount_ht: String(v)}))}
                  accentColor="blue"
                  label="Montant total de la facture"
                />
                <AmountInput
                  value={parseFloat(form.paid_amount) || 0}
                  onChange={(v) => setForm(f => ({...f, paid_amount: String(v)}))}
                  accentColor="blue"
                  label="Montant à payer maintenant"
                />
              </>
            ) : (
              <AmountInput
                value={parseFloat(form.amount_ht) || 0}
                onChange={(v) => setForm(f => ({...f, amount_ht: String(v)}))}
                accentColor="blue"
                label={form.pay_state === 'paid' ? 'Total à saisir' : 'Montant de la facture'}
              />
            )}
          </>)}

          {/* ===== STEP 4 ===== */}
          {step === 4 && (<>
            <div className="text-center pt-2">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: C_LIGHT }}>
                <Check className="w-7 h-7" style={{ color: C }} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Récapitulatif</h3>
              <p className="text-sm text-gray-500 mt-1">Vérifiez avant de confirmer</p>
            </div>

            {/* DOCUMENT */}
            <div className="rounded-xl p-4 bg-gray-50">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">Document</p>
              <div className="space-y-2">
                {[
                  ['Type', 'Facture fournisseur'],
                  ['Fournisseur', form.supplier_name || '-'],
                  ['N° facture', form.invoice_number || '-'],
                  ['Échéance', form.due_date ? format(new Date(form.due_date), 'dd MMM yyyy', { locale: fr }) : '-'],
                  ['Paiement', (() => {
                    const payAmt = getPayAmount();
                    if (form.pay_state === 'paid' || (payAmt >= totalTTC && totalTTC > 0)) return { text: 'Payée', color: '#16A34A' };
                    if (form.pay_state === 'partial' && payAmt > 0) return { text: `Partielle (${fmt(payAmt)} DA)`, color: '#EA580C' };
                    return { text: 'En attente', color: '#EA580C' };
                  })()],
                  ['Mode', form.pay_state !== 'unpaid' ? (PAY_METHODS.find(m => m.id === form.pay_method)?.label || '-') : '-'],
                ].map(([label, value], i) => (
                  <div key={i} className="flex justify-between text-sm border-b border-gray-200/60 pb-1.5 last:border-0 last:pb-0">
                    <span className="text-gray-500">{label}</span>
                    {typeof value === 'object' && value?.text ? (
                      <span className="font-semibold" style={{ color: value.color }}>{value.text}</span>
                    ) : (
                      <span className="font-semibold text-gray-900">{value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* MONTANTS */}
            <div className="rounded-xl p-4 bg-gray-50">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-3">Montants</p>
              <div className="space-y-2">
                {form.pay_state === 'unpaid' ? (
                  <>
                    <div className="flex justify-between text-sm border-b border-gray-200/60 pb-1.5"><span className="text-gray-500">HT</span><span className="font-bold">{fmt(parseFloat(form.amount_ht) || 0)} DA</span></div>
                    <div className="flex justify-between text-sm border-b border-gray-200/60 pb-1.5"><span className="text-gray-500">TVA {form.tva_rate}%</span><span className="font-bold">{fmt(tvaAmount)} DA</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">Total TTC</span><span className="font-bold" style={{ color: C }}>{fmt(totalTTC)} DA</span></div>
                  </>
                ) : form.pay_state === 'paid' ? (
                  <>
                    <div className="flex justify-between text-sm border-b border-gray-200/60 pb-1.5"><span className="text-gray-500">HT</span><span className="font-bold">{fmt(parseFloat(form.amount_ht) || 0)} DA</span></div>
                    <div className="flex justify-between text-sm border-b border-gray-200/60 pb-1.5"><span className="text-gray-500">TVA {form.tva_rate}%</span><span className="font-bold">{fmt(tvaAmount)} DA</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">Total TTC</span><span className="font-bold" style={{ color: C }}>{fmt(totalTTC)} DA</span></div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm border-b border-gray-200/60 pb-1.5"><span className="text-gray-500">Montant payé</span><span className="font-bold" style={{ color: '#16A34A' }}>{fmt(getPayAmount())} DA</span></div>
                    <div className="flex justify-between text-sm border-b border-gray-200/60 pb-1.5"><span className="text-gray-500">Reste à payer</span><span className="font-bold" style={{ color: '#EA580C' }}>{fmt(Math.max(totalTTC - getPayAmount(), 0))} DA</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-500">Total TTC</span><span className="font-bold" style={{ color: C }}>{fmt(totalTTC)} DA</span></div>
                  </>
                )}
              </div>
            </div>

            {/* Info box */}
            <div className="rounded-xl p-4 flex items-start gap-3" style={{ backgroundColor: C_BG }}>
              <Info className="flex-shrink-0 mt-0.5" size={18} style={{ color: C }} />
              <p className="text-sm" style={{ color: '#1E40AF' }}>
                {form.pay_state === 'unpaid' && "La facture sera enregistrée en attente de paiement."}
                {form.pay_state === 'paid' && "La facture sera marquée comme payée. Une dépense liée sera créée automatiquement."}
                {form.pay_state === 'partial' && `La facture sera marquée comme partielle. Le reste à payer sera mis à jour. Une dépense de ${fmt(getPayAmount())} DA sera créée.`}
              </p>
            </div>
          </>)}
        </div>

        {/* Footer standardisé */}
        <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-gray-100 bg-white rounded-b-2xl">
          <button
            onClick={() => step <= 1 ? onClose() : setStep(step - 1)}
            className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft size={16} />
            Retour
          </button>
          <span className="text-xs text-gray-500 font-medium">Étape {step} sur 4</span>
          {step < 4 ? (
            <button
              data-testid="invoice-wizard-next"
              onClick={() => canNext() && setStep(step + 1)}
              disabled={!canNext()}
              className="px-5 py-2 rounded-lg font-semibold text-sm text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              style={{ backgroundColor: C }}
            >
              Continuer
              <ArrowRight size={16} />
            </button>
          ) : (
            <button
              data-testid="invoice-wizard-submit"
              onClick={handleSubmit}
              className="px-5 py-2 rounded-lg font-semibold text-sm text-white bg-emerald-500 hover:bg-emerald-600 flex items-center gap-1.5 transition-colors"
            >
              <Check size={16} />
              Créer la facture
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvoiceWizard;
